package no.vareflow.app.sync

/**
 * Rules for the durable outbox of count writes waiting to reach Firestore, and for deciding what a
 * write does once the server's current entry is known. No Firebase types: FirebaseCloud supplies
 * the data and carries out the decision.
 * The durable JSON storage format lives in the app (data/local/CountOutboxJson).
 */
object CountOutbox {

    private fun sameEntry(a: PendingCountWrite, b: PendingCountWrite): Boolean =
        a.sessionId == b.sessionId && a.productId == b.productId && a.departmentId == b.departmentId

    /**
     * Adds [write] to the queue. An older, not yet sent ordinary write for the same entry is
     * replaced (only the newest value matters, and server writes are idempotent by event id).
     * Conflict-resolution writes are never dropped.
     */
    fun enqueue(outbox: List<PendingCountWrite>, write: PendingCountWrite): List<PendingCountWrite> =
        outbox.filterNot { sameEntry(it, write) && it.resolutionOfConflictId == null } + write

    /**
     * After [acked] was confirmed at [confirmedVersion], later queued writes for the same entry
     * were created on this device from the same stale base version. Moves them onto the confirmed
     * version so the device does not raise a conflict against its own edit.
     * Returns null when nothing needed to change.
     */
    fun rebase(outbox: List<PendingCountWrite>, acked: PendingCountWrite, confirmedVersion: Int): List<PendingCountWrite>? {
        var changed = false
        val rebased = outbox.map { item ->
            if (item.resolutionOfConflictId == null &&
                sameEntry(item, acked) &&
                item.baseVersion == acked.baseVersion &&
                item.baseVersion != confirmedVersion
            ) {
                changed = true
                item.copy(baseVersion = confirmedVersion)
            } else item
        }
        return if (changed) rebased else null
    }

    /**
     * Firestore error codes (by enum name) after which retrying the same write can never succeed,
     * e.g. the category was marked DONE or access was removed. Such writes leave the queue so
     * they do not block every later write. Everything else (offline, timeout, contention) is retried.
     */
    private val permanentFailureCodes = setOf(
        "PERMISSION_DENIED", "INVALID_ARGUMENT", "NOT_FOUND", "ALREADY_EXISTS", "OUT_OF_RANGE", "FAILED_PRECONDITION"
    )

    fun isPermanentFailure(codeName: String?): Boolean = codeName != null && codeName in permanentFailureCodes

    /** What a queued write should do, given the server's current count entry. */
    sealed interface Decision {
        /** This exact event is already on the server (a retry after a lost acknowledgement). */
        data class AlreadyApplied(val version: Int) : Decision
        /** The server already holds exactly the wanted value; nothing to write. */
        data class AlreadyEqual(val version: Int) : Decision
        /** Someone else changed the entry since this device read it; record a conflict instead. */
        data class Conflict(val remoteVersion: Int) : Decision
        /** Write the value as version [newVersion]. */
        data class Apply(val newVersion: Int) : Decision
    }

    /**
     * @param exists whether the server document exists
     * @param currentVersion the server's version (0 when missing)
     * @param lastEventId the event id that produced the server's current value
     */
    fun decide(
        write: PendingCountWrite,
        exists: Boolean,
        currentVersion: Int,
        lastEventId: String?,
        remoteQuantity: Double?,
        remoteNote: String
    ): Decision {
        if (lastEventId == write.eventId) return Decision.AlreadyApplied(currentVersion)
        if (exists && currentVersion != write.baseVersion && write.resolutionOfConflictId == null) {
            if (remoteQuantity == write.quantity && remoteNote == write.note) return Decision.AlreadyEqual(currentVersion)
            return Decision.Conflict(currentVersion)
        }
        return Decision.Apply(currentVersion + 1)
    }
}
